import * as React from 'react';
import ReactPaginate from 'react-paginate';

import { css, cx } from '@emotion/css';

import { styled } from '../theme';
import FlexRow from './FlexRow';
import SvgIcon from './SvgIcon';

const StyledSvgIcon = styled(SvgIcon)(({ theme, disabled }) => ({
    cursor: disabled ? 'default' : 'pointer',
    svg: {
        circle: {
            stroke: disabled ? theme.color.disabled : theme.color.primary1,
        },
        path: {
            fill: disabled ? theme.color.disabled : theme.color.primary1,
        },
    },
}));

const styledPagination = (isMinimized: boolean) =>
    cx(
        css({
            display: 'flex',
            margin: '20px auto',
            paddingLeft: 0,
            '> li': {
                display: 'inline',
                margin: `0 ${isMinimized ? 0 : 5}px`,
            },
            '> li:first-of-type > a, > li:first-of-type > span': {
                marginRight: '17px',
            },
            '> li:last-child > a, > li:last-child > span': {
                marginLeft: '17px',
            },
            '> li > a, > li > span': {
                outline: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '34px',
                height: '34px',
                borderRadius: '17px',
                background: 'none',
                position: 'relative',
                justifySelf: 'flex-start',
                color: '#1FBA59',
                textDecoration: 'none',
            },
            'li > a:hover, > li > span:hover, > li > a:focus, > li > span:focus': {
                color: '#1DAA52',
            },
            '> .active > a, > .active > span, > .active > a:hover, > .active > span:hover, > .active > a:focus, > .active > span:focus':
                {
                    color: '#16191C',
                    cursor: 'default',
                    background: 'grey',
                },
            '> .disabled > span, > .disabled > span:hover, > .disabled > span:focus, > .disabled > a, > .disabled > a:hover, > .disabled > a:focus':
                {
                    color: '#5B5E5F',
                    cursor: 'not-allowed',
                    backgroundColor: 'none',
                },
            '> .selected > span, > .selected > span:hover, > .selected > span:focus, > .selected > a, > .selected > a:hover, > .selected > a:focus':
                {
                    color: '#5B5E5F',
                    cursor: 'default',
                    background: '#F0F2F1',
                },
        })
    );

type Props = {
    totalItems: number;
    currentPage: number;
    onChangePage: (page) => void;
    rowsPerPage: number;
    isMinimized?: boolean;
};

export default class Pagination extends React.Component<Props> {
    handlePageClick = ({ selected }) => this.props.onChangePage(selected + 1);

    getMaxPage() {
        const { totalItems, rowsPerPage } = this.props;
        return Math.ceil(totalItems / rowsPerPage);
    }

    renderBackBtn = (currentPage: number) => (
        <StyledSvgIcon
            alt="back"
            width={34}
            height={34}
            src="/icons/ic-back.svg"
            disabled={currentPage === 1}
        />
    );

    renderNextBtn = (currentPage: number) => (
        <StyledSvgIcon
            alt="next"
            width={34}
            height={34}
            src="/icons/ic-next.svg"
            disabled={currentPage === this.getMaxPage()}
        />
    );

    render() {
        const {
            totalItems,
            rowsPerPage,
            currentPage,
            onChangePage,
            isMinimized = false,
            ...restProps
        } = this.props;
        const pageCount = Math.ceil(totalItems / rowsPerPage);

        return (
            <FlexRow {...restProps} align="center">
                <ReactPaginate
                    previousLabel={this.renderBackBtn(currentPage)}
                    nextLabel={this.renderNextBtn(currentPage)}
                    breakLabel="..."
                    forcePage={currentPage - 1}
                    pageCount={pageCount}
                    marginPagesDisplayed={isMinimized ? 1 : 2}
                    pageRangeDisplayed={isMinimized ? 1 : 3}
                    onPageChange={this.handlePageClick}
                    containerClassName={styledPagination(isMinimized)}
                />
            </FlexRow>
        );
    }
}
